import { prioritizeAndMapDropdownItems } from '@approvalmax/ui';
import { selectors } from 'modules/common';
import { State } from 'modules/data';
import { AsyncDataProvider } from 'modules/data-providers';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { FC, PropsWithChildren } from 'react';
import { useSelector } from 'react-redux';
import { useLazyGetCustomerContactsRaw } from 'shared/data/webApp/v1';
import { isOcrEnabledForTemplate } from 'shared/helpers';

import { parseXeroContact } from '../../../data/xero/XeroContact';
import { useGetRecognizedOcrAttachment } from '../../../hooks/useGetRecognizedOcrAttachment';
import { getRequest } from '../../../selectors/pageSelectors';
import { getRequestEditMode } from '../../../selectors/requestSelectors';
import { getRuleTypeByEditMode } from '../../../utils/helpers';

const CustomerContactDataProvider: FC<PropsWithChildren> = (props) => {
    const { children } = props;
    const [getCustomerContactsRaw] = useLazyGetCustomerContactsRaw();
    const request = useSelector(getRequest);
    const editMode = useSelector((state: State) => getRequestEditMode(state, request));
    const company = useSelector((state: State) => selectors.company.getCompanyById(state, request.companyId));

    const ocrAttachment = useGetRecognizedOcrAttachment(request);

    const shouldUseOcr = ocrAttachment && isOcrEnabledForTemplate(company, request.integrationCode);

    const { data, refetch } = useOcrData(request, ocrAttachment?.id);

    const onLoad = async (filterText: string | null, startFrom: number, count: number) => {
        const ruleType = getRuleTypeByEditMode(editMode);

        const response = await getCustomerContactsRaw({
            params: {
                query: {
                    requestId: request.id,
                    ruleType,
                    query: filterText,
                    startFrom,
                    rowNum: count,
                    companyId: request.companyId,
                },
            },
        });

        let parsedContacts = response.Contacts.map(parseXeroContact);

        if (shouldUseOcr) {
            const ocrData = data || (await refetch()).data;

            return prioritizeAndMapDropdownItems(parsedContacts, ocrData?.document?.customerIds);
        }

        return response.Contacts.map(parseXeroContact);
    };

    return (
        <AsyncDataProvider
            cacheStorageId={`requestOptions.customerContact.${request.id}.${editMode}`}
            pageSize={100}
            onLoad={onLoad}
        >
            {children}
        </AsyncDataProvider>
    );
};

export default CustomerContactDataProvider;
